import { get, push, ref, set, update, onValue, query, orderByChild, startAt, goOffline, goOnline } from "firebase/database";
import { database } from "./firebase";
import { logError, logEvent } from "./logs";
import { removeUndefinedValues } from "../util/util";

const isValidUid = (uid) => uid && uid !== "undefined";

export const getVersion = async () => {
    try {
        const snapshot = await get(ref(database, `/meta/version`));
        if (snapshot.exists()) {
            return snapshot.val();
        }
        return null;
    } catch (error) {
        logError(error, { function: "getVersion" });
        console.error(error);
        return null;
    }
};

export const getUserPermission = async (userId, setPermissionDate) => {
    try {
        const snapshot = await get(ref(database, `/users/${userId}/expiration_date`));
        if (snapshot.exists()) {
            setPermissionDate(snapshot.val());
            return snapshot.val();
        }
        return null;
    } catch (error) {
        logError(error, { function: "getUserPermission" });
        console.error(error);
        return null;
    }
};

export const getUserBeta = async (userId, setUserBeta) => {
    try {
        const snapshot = await get(ref(database, `/users/${userId}/beta_user`));
        if (snapshot.exists()) {
            setUserBeta(true);
            return true;
        }
        setUserBeta(false);
        return false;
    } catch (error) {
        logError(error, { function: "getUserBeta" });
        console.error(error);
        return false;
    }
};

export const getUserWooviCustomer = async (userId) => {
    if (!userId) {
        return null;
    }

    try {
        const snapshot = await get(ref(database, `/users/${userId}/billing/wooviCustomer`));
        if (snapshot.exists()) {
            return snapshot.val()?.customer || snapshot.val();
        }
        return null;
    } catch (error) {
        logError(error, { function: "getUserWooviCustomer" });
        console.error(error);
        return null;
    }
};

export const setUserWooviCustomer = async (userId, customer) => {
    if (!userId) {
        return false;
    }

    try {
        const updates = {
            [`/users/${userId}/billing/wooviCustomer`]: removeUndefinedValues({
                customer,
                updatedAt: new Date().toISOString(),
            }),
        };
        await update(ref(database), updates);
        return true;
    } catch (error) {
        logError(error, { function: "setUserWooviCustomer" });
        console.error(error);
        return false;
    }
};

export const getUserLegacyAccess = async (userId, setLegacyAccess = () => {}) => {
    try {
        const snapshot = await get(ref(database, `/users/${userId}/legacy_web_access`));
        if (snapshot.exists()) {
            const legacyAccess = snapshot.val() !== false;
            setLegacyAccess(legacyAccess);
            return legacyAccess;
        }

        setLegacyAccess(true);
        return true;
    } catch (error) {
        logError(error, { function: "getUserLegacyAccess" });
        console.error(error);
        setLegacyAccess(true);
        return true;
    }
};

export const initializeUserLegacyAccess = async (user, options = {}) => {
    if (!user?.uid) {
        return false;
    }

    const {
        legacyWebAccess = false,
        name = user.displayName || null,
        email = user.email || null,
    } = options;

    try {
        const updates = removeUndefinedValues({
            [`/users/${user.uid}/legacy_web_access`]: legacyWebAccess,
            [`/users/${user.uid}/created_at`]: new Date().toISOString(),
            [`/users/${user.uid}/profile/name`]: name,
            [`/users/${user.uid}/profile/email`]: email,
        });
        await update(ref(database), updates);
        return true;
    } catch (error) {
        logError(error, { function: "initializeUserLegacyAccess", userId: user.uid });
        console.error(error);
        return false;
    }
};

export const updateActions = async (userId, analysisUid, side, groupIndex, actions) => {
    if (
        !isValidUid(analysisUid) ||
        (side !== "left" && side !== "right") ||
        groupIndex === undefined ||
        groupIndex === null ||
        actions === undefined ||
        actions === null
    ) {
        logEvent("updateActions", { message: "invalid parameters", userId, analysisUid, side, groupIndex });
        console.error("updateActions: invalid parameters");
        return false;
    }
    try {
        const path = `/users/${userId}/analises_dados/${analysisUid}/${side}Actions/${groupIndex}/actions/`;
        const updates = {};
        updates[path] = actions;
        const dbRef = ref(database);
        await update(dbRef, updates);
        return true;
    } catch (error) {
        logError(error, { function: "updateActions" });
        console.error(error);
        return false;
    }
};

export const getUserAnalysisList = async (userId, includeDeleted = false) => {
    try {
        const snapshot = await get(ref(database, `/users/${userId}/analises`));
        if (snapshot.exists()) {
            const data = snapshot.val();
            const filteredData = Object.fromEntries(
                Object.entries(data).filter(([_, value]) => (includeDeleted || !value.deleted) && value.uid)
            );
            return filteredData;
        } else {
            //logEvent("getUserAnalysisList", { message: "Nenhuma Análise encontrada!" });
            //console.log("Nenhuma Análise encontrada!");
            return null;
        }
    } catch (error) {
        logError(error, { function: "getUserAnalysisList" })
        console.error(error);
        return null;
    }
};

export const getUserAnalysisListSince = async (userId, lastSync) => {
    try {
        const analysesRef = ref(database, `/users/${userId}/analises`);
        const q = query(analysesRef, orderByChild('lastUpdate'), startAt(lastSync));
        const snapshot = await get(q);
        if (snapshot.exists()) {
            const data = snapshot.val();
            const filteredData = Object.fromEntries(
                Object.entries(data).filter(([_, value]) => value.uid)
            );
            return filteredData;
        }
        return null;
    } catch (error) {
        logError(error, { function: "getUserAnalysisListSince" });
        console.error(error);
        return null;
    }
};

export const deleteUserAnalysis = async (userId, analysisUid) => {
    if (!isValidUid(analysisUid)) {
        logEvent("deleteUserAnalysis", { message: "invalid analysisUid", userId, analysisUid });
        console.error("deleteUserAnalysis: invalid analysisUid");
        return null;
    }
    try {
        const dbAnalise = ref(database, `/users/${userId}/analises/${analysisUid}`);
        const now = new Date().toISOString();
        await update(dbAnalise, {
            deleted: true,
            deletedAt: now,
            lastUpdate: now,
        });
        console.log("Análise marcada como deletada com sucesso!");
        return true;
    } catch (error) {
        logError(error, { function: "deleteUserAnalysis" })
        console.error("Erro ao marcar análise como deletada:", error);
        return null;
    }
}

export const createUserAnalysis = async (userId, analysis, analysisData) => {
    try {
        const dbAnalysisWithoutUid = ref(database, `/users/${userId}/analises`);
        const newAnalysisRef = push(dbAnalysisWithoutUid);
        const analysisUid = newAnalysisRef.key;
        const updates = {
            [`/users/${userId}/analises/${analysisUid}`]: {
                ...analysis,
                uid: analysisUid,
            },
            [`/users/${userId}/analises_dados/${analysisUid}`]: {
                ...analysisData,
                uid: analysisUid,
            },
        };
        const dbRef = ref(database);
        await update(dbRef, updates);
        return analysisUid;
    } catch (error) {
        logError(error, { function: "createUserAnalysis" })
        console.error("Erro ao criar análise:", error);
        return null;
    }
}

export const getUserAnalysisData = async (userId, analysisUid) => {
    try {
        const snapshot = await get(ref(database, `/users/${userId}/analises_dados/${analysisUid}`));
        if (snapshot.exists()) {
            const data = snapshot.val();
            return data;
        } else {
            const analysisRef = ref(database, `/users/${userId}/analises/${analysisUid}`);
            const analysisSnap = await get(analysisRef);
            if (analysisSnap.exists()) {
                const now = new Date().toISOString();
                await update(analysisRef, { deleted: true, deletedAt: now, lastUpdate: now });
            }
            //logEvent("getUserAnalysisData", { message: "Dados da análise não encontrados!", analysisId: analysisUid });
            //console.log("Dados da análise não encontrados!");
            return null;
        }
    } catch (error) {
        logError(error, { function: "getUserAnalysisData" })
        console.error(error);
        return null;
    }
};

export const updateAnalysisLastUpdate = async (userId, analysisUid, lastUpdate) => {
    if (!isValidUid(analysisUid)) {
        logEvent("updateAnalysisLastUpdate", { message: "invalid analysisUid", userId, analysisUid });
        console.error("updateAnalysisLastUpdate: invalid analysisUid");
        return null;
    }
    try {
        const dbRef = ref(database, `/users/${userId}/analises/${analysisUid}`);
        await update(dbRef, { lastUpdate });
        return true;
    } catch (error) {
        logError(error, { function: "updateAnalysisLastUpdate" })
        console.error(error);
        return null;
    }
};

export const setAnalysisActionData = async (userId, analysisUid, side, groupIndex, actionIndex, data) => {
    if (!isValidUid(analysisUid)) {
        logEvent("setAnalysisActionData", { message: "invalid analysisUid", userId, analysisUid });
        console.error("setAnalysisActionData: invalid analysisUid");
        return null;
    }
    try {
        const path = `/users/${userId}/analises_dados/${analysisUid}/${side}Actions/${groupIndex}/actions/${actionIndex}/data/`;
        const cleanData = removeUndefinedValues(data);
        await set(ref(database, path), cleanData);
        return true;
    } catch (error) {
        logError(error, { function: "setAnalysisActionData" })
        console.error(error);
        return null;
    }
};

export const updateMultipleNodes = async (
    userId,
    analysisUid,
    analysisUpdates = {},
    analysisDataUpdates = {}
) => {
    if (!isValidUid(analysisUid)) {
        logEvent("updateMultipleNodes", { message: "invalid analysisUid", userId, analysisUid });
        console.error("updateMultipleNodes: invalid analysisUid");
        return null;
    }
    try {
        const updates = {};
        Object.entries(analysisUpdates).forEach(([childPath, value]) => {
            updates[`/users/${userId}/analises/${analysisUid}/${childPath}`] = value;
        });
        Object.entries(analysisDataUpdates).forEach(([childPath, value]) => {
            updates[`/users/${userId}/analises_dados/${analysisUid}/${childPath}`] = value;
        });
        const dbRef = ref(database);
        const cleanUpdates = removeUndefinedValues(updates);
        await update(dbRef, cleanUpdates);
        return true;
    } catch (error) {
        logError(error, { function: "updateMultipleNodes" })
        console.error(error);
        return null;
    }
};

export const saveTelestratorInfo = async (userId, analysisUid, telestratorData, favoriteColors) => {
    if (!isValidUid(analysisUid)) {
        logEvent("saveTelestratorInfo", { message: "invalid analysisUid", userId, analysisUid });
        console.error("saveTelestratorInfo: invalid analysisUid");
        return null;
    }
    try {
        const updates = {
            [`/users/${userId}/analises_dados/${analysisUid}/telestratorData`]: telestratorData,
            [`/users/${userId}/analises_dados/${analysisUid}/favoriteColors`]: favoriteColors,
        };
        const cleanData = removeUndefinedValues(updates);
        const dbRef = ref(database);
        await update(dbRef, cleanData);
        return true;
    } catch (error) {
        logError(error, { function: "saveTelestratorInfo" });
        console.error(error);
        return null;
    }
};

export const deleteTelestratorData = async (userId, analysisUid, telestratorData) => {
    if (!isValidUid(analysisUid)) {
        logEvent("deleteTelestratorData", { message: "invalid analysisUid", userId, analysisUid });
        console.error("deleteTelestratorData: invalid analysisUid");
        return null;
    }
    try {
        const updates = {
            [`/users/${userId}/analises_dados/${analysisUid}/telestratorData`]: telestratorData,
        };
        const cleanData = removeUndefinedValues(updates);
        const dbRef = ref(database);
        await update(dbRef, cleanData);
        return true;
    } catch (error) {
        logError(error, { function: "deleteTelestratorData" });
        console.error(error);
        return null;
    }
};

export const onConnectionChange = (callback) => {
    try {
        const connectedRef = ref(database, '.info/connected');
        const unsubscribeFirebase = onValue(connectedRef, callback);

        const handleOnline = () => {
            goOnline(database);
            callback({ val: () => true });
        };

        const handleOffline = () => {
            goOffline(database);
            callback({ val: () => false });
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('online', handleOnline);
            window.addEventListener('offline', handleOffline);
        }

        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            handleOffline();
        }

        return () => {
            unsubscribeFirebase();
            if (typeof window !== 'undefined') {
                window.removeEventListener('online', handleOnline);
                window.removeEventListener('offline', handleOffline);
            }
        };
    } catch (error) {
        logError(error, { function: 'onConnectionChange' });
        console.error(error);
        return null;
    }
};
